import * as React from 'react';
import './Admin.less';
import { Form, Input, Button, Select, Divider, message, Spin, Checkbox, Tooltip, Row, Col } from 'antd';
import { FormComponentProps } from 'antd/lib/form/Form';
import { withRouter, Link, Redirect } from 'react-router-dom';
import bindAllOfThis from "src/utils/BindThisHelper"; // 'this' binder
import Routes from 'src/core/Routes';
import { Translate, withLocalize, LocalizeContextProps } from 'react-localize-redux';
import { PageProps } from 'src/models/common/ComponentProps';

import TranslationDTO from 'src/models/generic/TranslationDTO';
import AddActivityQuestionCategoryDTO from 'src/models/activity/AddActivityQuestionCategoryDTO';
import UpdateActivityQuestionCategoryDTO from 'src/models/activity/UpdateActivityQuestionCategoryDTO';
import KeyValueListDTO from 'src/models/generic/KeyValueListDTO';
import KeyValueDTO from 'src/models/generic/KeyValueDTO';
import ActivityApiController from 'src/api/ActivityApiController';
import CultureApiController from 'src/api/CultureApiController';
import TextArea from 'antd/lib/input/TextArea';
import Languages, { Language } from 'src/constants/Languages';



interface RouteParams {
    activityId: string;
    activityQuestionCategoryId: string;
}

interface AddEditActivityQuestionCategoryProps extends PageProps<RouteParams>, LocalizeContextProps { }

interface AddEditActivityQuestionCategoryState {
    loading: boolean;
    isSubmitting: boolean;
    currentActivityQuestionCategory: UpdateActivityQuestionCategoryDTO;
    allCultures: Language[];
    selectedCultureId: number;
    redirect: boolean;
}

interface ActivityQuestionCategoryFormProps {
    currentActivityQuestionCategory: UpdateActivityQuestionCategoryDTO;
    allCultures: Language[];
    selectedCultureId: number;
    translate: (id: any) => any;
    onChange: (changedFields: any[]) => void;
}

// Form
let ActivityQuestionCategoryForm = Form.create({
    onFieldsChange(props: ActivityQuestionCategoryFormProps, changedFields: any[]) {
        props.onChange(changedFields);
    }
})(class extends React.Component<ActivityQuestionCategoryFormProps & FormComponentProps> {
    constructor(props: ActivityQuestionCategoryFormProps & FormComponentProps) {
        super(props);
    }

    private generateOptionsForSelect(keyValues: KeyValueDTO<string, number>[]): JSX.Element[] {
        return keyValues.map(keyValue => <Select.Option value={keyValue.value} key={keyValue.key} title={keyValue.key}>{keyValue.key}</Select.Option>);
    }

    private generateLanguageOptions() {
        // Only available languages
        return Languages.All.filter(x => x.isAvailable)
            .map(x => <Select.Option key={`cultureIds${x.id}`} value={x.id}>{x.name} ({x.code})</Select.Option>);
    }

    private renderTranslations(): JSX.Element[] {
        if (this.props.currentActivityQuestionCategory) {
            let translations = this.props.currentActivityQuestionCategory.translations;

            return translations.map((translation: TranslationDTO, index: number) => {
                let id = "translations[" + index + "].text";
                let culture = this.props.allCultures.find(culture => culture.id === translation.cultureId).code.toLowerCase();
                let translationId = "admin.addEditActivityQuestionCategory." + culture;

                return this.renderStandardFormItem(
                    translationId,
                    id,
                    translation.text,
                    <TextArea rows={4} autoComplete="off" />,
                    this.props.selectedCultureId === translation.cultureId
                );
            });
        }
    }

    private renderStandardFormItem(translationId: string, id: string, initialValue, inputType, visible: boolean = true, required: boolean = true) {
        let translatedLabel = this.props.translate(translationId);

        if (visible) {
            return (
                <Form.Item label={translatedLabel}>
                    {this.props.form.getFieldDecorator(id, {
                        initialValue: initialValue,
                        rules: [{ required: required, message: this.props.translate('admin.common.requiredRule') }]
                    })(inputType)}
                </Form.Item>
            );
        }
    }

    render() {
        return (
            <Row>
                <Col span={12}>
                    <Form className="admin-form">
                        {this.renderStandardFormItem(
                            "admin.addEditActivityQuestionCategory.formItem1",
                            "selectedCultureId",
                            this.props.selectedCultureId,
                            <Select>{this.generateLanguageOptions()}</Select>
                        )}

                        {this.renderTranslations()}
                    </Form>
                </Col>
            </Row>
        );
    }
});

class AddEditActivityQuestionCategory extends React.Component<AddEditActivityQuestionCategoryProps, AddEditActivityQuestionCategoryState> {
    private formRef: React.Component<FormComponentProps>;

    constructor(props: AddEditActivityQuestionCategoryProps) {
        super(props);
        bindAllOfThis(this, AddEditActivityQuestionCategory.prototype);

        let activityId = parseInt(this.props.match.params.activityId, 10);
        let activityQuestionCategoryId = parseInt(this.props.match.params.activityQuestionCategoryId, 10);

        let currentActivityQuestionCategory: UpdateActivityQuestionCategoryDTO = {
            activityQuestionCategoryId: activityQuestionCategoryId,
            translations: [],
            activityId: activityId
        };

        this.state = {
            loading: true,
            isSubmitting: false,
            redirect: false,
            allCultures: [],
            selectedCultureId: 0,
            currentActivityQuestionCategory: currentActivityQuestionCategory
        };
    }

    componentWillMount() {
        let cultures = Languages.All.filter((l) => l.isAvailable);

        this.setState({
            allCultures: cultures,
            selectedCultureId: cultures[0].id
        }, this.fillForm);

    }

    private fillForm(): void {
        if (this.state.currentActivityQuestionCategory.activityQuestionCategoryId) {
            ActivityApiController.getActivityQuestionCategory(this.state.currentActivityQuestionCategory.activityQuestionCategoryId).then((result) => {
                this.setState({
                    loading: false,
                    currentActivityQuestionCategory: result.data
                });
            }).catch(e => {
                message.error(this.props.translate('admin.addEditActivityQuestionCategory.messageRetrieveError'));
            });
        } else {
            let translations = this.state.allCultures.map(
                (culture: Language): TranslationDTO => {
                    let translation: TranslationDTO = {
                        cultureId: culture.id,
                        text: null
                    };

                    return translation;
                }
            );

            let activityQuestionCategory = {
                translations: translations
            } as UpdateActivityQuestionCategoryDTO;

            this.setState({
                currentActivityQuestionCategory: { ...this.state.currentActivityQuestionCategory, ...activityQuestionCategory },
                loading: false
            });
        }
    }

    private translationsAreCompleted(): boolean {
        let translations = this.state.currentActivityQuestionCategory.translations;
        let emptyTranslations = translations.filter(translation =>
            translation.text === null || translation.text === ""
        );

        return emptyTranslations.length === 0;
    }

    private handleInputChange(changedFields: any[]): void {
        let newState = {};
        for (var key in changedFields) {
            if (changedFields.hasOwnProperty(key)) {
                let field: any = changedFields[key];

                // If key is the nested property
                if (key === 'translations') {
                    let fieldKey = '';
                    for (var fieldName in field) {
                        if (field.hasOwnProperty(fieldName)) {
                            fieldKey = fieldName;
                            break;
                        }
                    }

                    newState[key] = [...this.state.currentActivityQuestionCategory[key]];
                    newState[key][fieldKey].text = field[fieldKey].text.value;
                } else if (key === 'selectedCultureId') {
                    this.setState({ selectedCultureId: field.value });
                    return;
                } else {
                    newState[key] = field.value;
                }
            }
        }
        this.setState({
            currentActivityQuestionCategory: { ...this.state.currentActivityQuestionCategory, ...newState }
        });
    }

    private addActivityQuestionCategory(): void {
        let addActivityQuestionCategoryDTO: AddActivityQuestionCategoryDTO = {
            translations: this.state.currentActivityQuestionCategory.translations,
            activityId: this.state.currentActivityQuestionCategory.activityId
        };

        ActivityApiController.addActivityQuestionCategory(addActivityQuestionCategoryDTO)
            .then(result => {
                message.success(this.props.translate('admin.addEditActivityQuestionCategory.messageAdd'));
                this.setState({ redirect: true, isSubmitting: false });
            }).catch(e => {
                message.error(this.props.translate('admin.addEditActivityQuestionCategory.messageAddError'));
                this.setState({ isSubmitting: false });
            });
    }

    private updateActivityQuestionCategory(): void {
        ActivityApiController.updateActivityQuestionCategory(this.state.currentActivityQuestionCategory)
            .then(result => {
                message.success(this.props.translate('admin.addEditActivityQuestionCategory.messageUpdate'));
                this.setState({ redirect: true, isSubmitting: false });
            }).catch(e => {
                message.error(this.props.translate('admin.addEditActivityQuestionCategory.messageUpdateError'));
                this.setState({ isSubmitting: false });
            });
    }

    private handleSubmit(e): void {
        e.preventDefault();

        this.formRef.props.form.validateFields((error: any, values: any) => {
            if (!error) {
                this.setState({ isSubmitting: true });
                this.state.currentActivityQuestionCategory.activityQuestionCategoryId ? this.updateActivityQuestionCategory() : this.addActivityQuestionCategory();
            }
        });
    }

    private handleCancel(e): void {
        this.setState({ redirect: true });
    }

    private renderSaveButton(): JSX.Element {
        if (this.translationsAreCompleted()) {
            return (
                <Button type="primary" onClick={this.handleSubmit} loading={this.state.isSubmitting}>
                    {this.props.translate("admin.common.saveButton")}
                </Button>
            );
        }

        return (
            <Tooltip placement="bottom" title={this.props.translate('admin.addEditActivityQuestionCategory.saveTooltip')} arrowPointAtCenter>
                <Button type="primary" disabled>
                    {this.props.translate("admin.common.saveButton")}
                </Button>
            </Tooltip>
        );
    }

    render() {
        if (this.state.redirect) {
            return <Redirect to={Routes.getRoute(Routes.ADMIN_ADD_EDIT_ACTIVITY, this.state.currentActivityQuestionCategory.activityId)} push />;
        } else if (this.state.loading) {
            return <Spin />;
        }

        let formProps = {
            currentActivityQuestionCategory: this.state.currentActivityQuestionCategory,
            allCultures: this.state.allCultures,
            selectedCultureId: this.state.selectedCultureId,
            translate: this.props.translate,
            onChange: this.handleInputChange
        } as ActivityQuestionCategoryFormProps;

        return (
            <div>
                <h2>
                    {this.props.translate('admin.addEditActivityQuestionCategory.addHeader')}
                </h2>

                <ActivityQuestionCategoryForm wrappedComponentRef={ref => this.formRef = ref} {...formProps} />

                <div>
                    {this.renderSaveButton()}
                    <Divider type="vertical" />
                    <Button onClick={this.handleCancel}>
                        {this.props.translate("common.back")}
                    </Button>
                </div>
            </div>
        );
    }
}

export default withLocalize(AddEditActivityQuestionCategory);



// WEBPACK FOOTER //
// ./src/pages/admin/AddEditActivityQuestionCategory.tsx